import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useEventStore } from '@/state/stores/use-event-store';
import { resolveLiveBindingFlowId } from '@/helpers/object.id.helper';
import { requiresEventSlotForLiveBinding } from '@/helpers/live-binding-context.helper';
import { useGetPlayersQuery } from '@/state/queries/players/use-get-players';
import { ConfidenceControlOverlay } from './confidence-control-overlay';
import { ConfidenceQaDialog } from './confidence-qa-dialog';
import { useConfidenceControlQuery } from '@/state/queries/player/use-confidence';
import type { QaChoice } from './confidence-qa.helper';

type OverlayRequest = {
  panel: 'time' | 'message';
  slotId?: string;
  sectionTitle: string;
};

type QaRequest = {
  slotId: string;
  originId?: string;
  sectionTitle: string;
  fullMinutes?: number;
};

type ConfidenceRundownValue = {
  hasConfidence: boolean;
  entityId?: string;
  openControl: (request: OverlayRequest) => void;
  qa: { flow: QaChoice; overrides: Record<string, QaChoice> } | null;
  openQa: (request: QaRequest) => void;
};

const ConfidenceRundownContext = createContext<ConfidenceRundownValue | null>(null);

export function useConfidenceRundown() {
  return useContext(ConfidenceRundownContext);
}

/** One players query and one control dialog for a whole rundown. */
export function ConfidenceRundownProvider({ children }: { children: ReactNode }) {
  const flow = useFlowStore((s) => s.flow);
  const event = useEventStore((s) => s.event);
  const [searchParams] = useSearchParams();
  const eventSlotId = searchParams.get('eventSlotId');
  const bindingFlowId = flow ? resolveLiveBindingFlowId(flow, eventSlotId ?? undefined) : undefined;
  const slotContextRequired = requiresEventSlotForLiveBinding({
    multiFlow: event?.multiFlow,
    eventSlotId,
  });
  const { data: players = [] } = useGetPlayersQuery(
    bindingFlowId
      ? {
          entityId: bindingFlowId,
          entityType: 'Flow',
          ...(eventSlotId ? { eventSlotId } : {}),
        }
      : undefined,
    { enabled: Boolean(bindingFlowId) && !slotContextRequired },
  );
  const hasConfidence = players.some((player) => player.type === 'confidencePlayer');
  const { data: confidence } = useConfidenceControlQuery(
    bindingFlowId ? { entityType: 'Flow', entityId: bindingFlowId } : undefined,
  );
  const [overlay, setOverlay] = useState<OverlayRequest | null>(null);
  const [qaRequest, setQaRequest] = useState<QaRequest | null>(null);
  const openControl = useCallback((request: OverlayRequest) => setOverlay(request), []);
  const openQa = useCallback((request: QaRequest) => setQaRequest(request), []);
  const qa = useMemo(() => {
    if (!confidence?.settings) return null;
    return {
      flow: {
        enabled: confidence.settings.qaForTalks,
        minutes: confidence.settings.qaMinutes,
      },
      overrides: confidence.qaOverrides ?? {},
    };
  }, [confidence]);
  const value = useMemo(
    () => ({ hasConfidence, entityId: bindingFlowId, openControl, qa, openQa }),
    [hasConfidence, bindingFlowId, openControl, qa, openQa],
  );

  return (
    <ConfidenceRundownContext.Provider value={value}>
      {children}
      {bindingFlowId && overlay ? (
        <ConfidenceControlOverlay
          open
          panel={overlay.panel}
          onClose={() => setOverlay(null)}
          entityType="Flow"
          entityId={bindingFlowId}
          slotId={overlay.slotId}
          sectionTitle={overlay.sectionTitle}
        />
      ) : null}
      {bindingFlowId && qa && qaRequest ? (
        <ConfidenceQaDialog
          open
          onClose={() => setQaRequest(null)}
          entityId={bindingFlowId}
          slotId={qaRequest.slotId}
          originId={qaRequest.originId}
          sectionTitle={qaRequest.sectionTitle}
          fullMinutes={qaRequest.fullMinutes}
          flow={qa.flow}
          overrides={qa.overrides}
        />
      ) : null}
    </ConfidenceRundownContext.Provider>
  );
}
